import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EDC29C] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-[#EDC29C] text-[#161616] shadow-sm hover:bg-[#E5B889] active:bg-[#DDA976] border border-[#EDC29C]/20",
        destructive:
          "bg-[#ED9C9C] text-[#161616] shadow-sm hover:bg-[#E58989] active:bg-[#DD7676] border border-[#ED9C9C]/20",
        success:
          "bg-[#9CEDC7] text-[#161616] shadow-sm hover:bg-[#89E5B8] active:bg-[#76DDA9] border border-[#9CEDC7]/20",
        warning:
          "bg-[#EDC29C] text-[#161616] shadow-sm hover:bg-[#E5B889] active:bg-[#DDA976] border border-[#EDC29C]/20",
        info:
          "bg-[#A49CED] text-[#161616] shadow-sm hover:bg-[#9189E5] active:bg-[#7E76DD] border border-[#A49CED]/20",
        outline:
          "border border-[#EDC29C]/40 bg-transparent text-[#EDC29C] shadow-sm hover:bg-[#EDC29C]/10 hover:border-[#EDC29C]/60 active:bg-[#EDC29C]/20",
        secondary:
          "bg-[#2C2C2C] text-[#EDC29C] shadow-sm hover:bg-[#3C3C3C] active:bg-[#4C4C4C] border border-white/10",
        ghost: "text-[#FAF1E9] hover:bg-[#EDC29C]/10 active:bg-[#EDC29C]/20",
        link: "text-[#EDC29C] underline-offset-4 hover:underline hover:text-[#E5B889]",
      },
      size: {
        default: "h-10 px-4 py-2 text-sm",
        sm: "h-8 rounded-lg px-3 text-xs",
        lg: "h-12 rounded-lg px-6 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const ShadcnButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
ShadcnButton.displayName = "ShadcnButton";

export { ShadcnButton, buttonVariants };
